/**
 * Aero Extension — Persistent product rail
 * ========================================
 * Behaviour for snippets/aero-extension-product-rail.liquid. The DS ships CSS only, so
 * the open/close contract is ours:
 *
 *   .air-product-rail          [data-air-open]       panel visible
 *   .air-cart-push (the shell) [data-air-rail-open]  page shifts by the rail width
 *
 * The two are deliberately separate attributes on separate elements. Since 2026-09-01 the
 * rail mounts INSIDE the shell root (outside __track) so its edge tab and bubbles carry a
 * live air-cart-push__chrome and ride the page shift — see the snippet's header comment.
 * The initial desktop state is a theme setting (data-aero-rail-default on the panel):
 * "open" docks the panel on arrival for viewports >=1024px, via bootOpen() below.
 *
 * ONE PANEL PUSHES AT A TIME. The DS guards its own half:
 *     .air-cart-push[data-air-rail-open]:not([data-air-open])
 * so an open cart always wins and the two shifts can never sum into an unusably narrow
 * page. We do the matching behavioural half here: opening the cart collapses the rail.
 *
 * Everything is delegated on `document`, matching aero-component-cart-drawer.js and
 * aero-extension-product-quickview.js, so injected markup works with no registration.
 */
(function () {
  var RAIL = '[data-aero-product-rail]';
  var STORAGE_KEY = 'aeroProductRailOpen';

  function rail() {
    return document.querySelector(RAIL);
  }

  function shell(railEl) {
    var sel = railEl && railEl.getAttribute('data-aero-rail-shell');
    return (sel && document.querySelector(sel)) || document.querySelector('[data-aero-cart-drawer]');
  }

  function toggles() {
    return document.querySelectorAll('[data-aero-product-rail-open]');
  }

  function bubbles() {
    return document.querySelector('[data-aero-product-bubbles]');
  }

  function isOpen(railEl) {
    return !!(railEl && railEl.hasAttribute('data-air-open'));
  }

  /* The edge tab now rides the page shift (air-cart-push__chrome, live since the
     2026-09-01 move inside the shell root) — but it still hides while the rail is open:
     a tab poking out of the panel's own left edge would read as a second, redundant
     close control. The panel has its own close.

     The product bubbles are a merchant choice while the rail is open
     (settings.product_rail_bubbles_when_open, carried on data-aero-bubbles-when-open):
     with the panel already merchandising the same three products, the bubbles beside it
     can read as clutter. Hidden via the DS utility class rather than the `hidden`
     attribute: .air-fab-stack sets display:flex at equal specificity, which would
     silently beat the UA's [hidden] rule — .air-display-none is later in the cascade,
     so it actually wins. They always return the moment the rail closes. */
  function syncChrome(open) {
    toggles().forEach(function (t) {
      t.setAttribute('aria-expanded', open ? 'true' : 'false');
      t.hidden = open;
    });
    var stack = bubbles();
    if (stack) {
      var railEl = rail();
      var hideWhileOpen = !!railEl && railEl.getAttribute('data-aero-bubbles-when-open') === 'false';
      stack.classList.toggle('air-display-none', open && hideWhileOpen);
    }
  }

  /* Focus management. The edge tab is hidden while the rail is open — which means the
     element the user just activated disappears. Without moving focus, the browser drops it to <body>: focus is lost,
     and because the rail is at the END of the document the close button is then ~a whole page
     of tabbing away. So: move focus INTO the panel on open, and restore it to the tab on close.

     Deliberately NOT a focus trap. The panel is non-modal (the page stays interactive beside
     it), so trapping would be wrong — a user must be able to tab out into the page.

     `moveFocus` is false when the panel is restored from sessionStorage on page load: stealing
     focus on load would hijack the keyboard and skip past the page's own content. */
  function focusInto(railEl) {
    var target = railEl.querySelector('[data-aero-product-rail-close]') ||
                 railEl.querySelector('a, button');
    if (target) target.focus();
  }

  function restoreFocus() {
    var t = document.querySelector('[data-aero-product-rail-open]');
    if (t && !t.hidden) t.focus();
  }

  /* Is the rail actually renderable, or has an ancestor been display:none'd?

     Originally built for the A/B test (closed 2026-09-01, rail now permanent), kept as a
     cheap defensive guard: ANY future display:none ancestor — a new test wrapper, a theme
     experiment, an app embed — would recreate the same failure. During the test the
     framework hid the losing arm by setting `display: none` on its [data-ab-variant]
     wrapper — it did NOT remove the markup — so
     the rail's DOM and this script were live for CONTROL visitors too. Without this guard a
     control visitor whose sessionStorage still remembered the rail as open (was variant B,
     cookie cleared or expired, then reassigned to A) got `data-air-rail-open` on the shell
     and the page pushed 384px aside with NOTHING in the gap. Measured: main 1056px in a
     1440px viewport. That degrades the CONTROL arm, which would inflate the treatment's
     apparent lift — the worst kind of experiment bug, because it flatters the thing being
     tested.

     getClientRects() is the right probe: a display:none ancestor yields zero rects, while
     the rail's own CLOSED state (position:fixed, visibility:hidden, translateX(100%)) is
     still laid out and yields one. A visibility or transform check could not tell those
     two apart. */
  function isRenderable(railEl) {
    return !!railEl && railEl.getClientRects().length > 0;
  }

  function open(railEl, moveFocus) {
    railEl = railEl || rail();
    if (!railEl || isOpen(railEl)) return;
    /* Never shift the page for a rail that cannot be seen — see isRenderable(). */
    if (!isRenderable(railEl)) return;

    /* The cart owns the shift while it is open — don't fight it. The panel still opens
       (the visitor asked for it); only the page shift waits, and the DS's :not() guard
       already expresses that in CSS. */
    var sh = shell(railEl);
    railEl.setAttribute('data-air-open', '');
    if (sh) sh.setAttribute('data-air-rail-open', '');
    syncChrome(true);
    persist(true);
    if (moveFocus !== false) focusInto(railEl);
  }

  function close(railEl, moveFocus) {
    railEl = railEl || rail();
    if (!railEl) return;
    var wasOpen = isOpen(railEl);
    var sh = shell(railEl);
    railEl.removeAttribute('data-air-open');
    if (sh) sh.removeAttribute('data-air-rail-open');
    syncChrome(false);
    persist(false);
    /* Only reclaim focus if it is currently inside the panel we just hid — otherwise the
       user has moved on (or the cart took over) and yanking focus back would be worse. */
    if (wasOpen && moveFocus !== false) {
      var a = document.activeElement;
      if (!a || a === document.body || (railEl.contains(a))) restoreFocus();
    }
  }

  /* Remember the collapsed/expanded choice so the rail doesn't re-open on every page and
     fight a reader who closed it. sessionStorage, not localStorage: "I don't want this
     right now" is a session-scoped intent, not a permanent preference. */
  function persist(isOpenNow) {
    try {
      sessionStorage.setItem(STORAGE_KEY, isOpenNow ? '1' : '0');
    } catch (e) {
      /* private mode / quota — the rail just won't remember, which is fine */
    }
  }

  /* Boot-time open — restoring a remembered open state, or honouring the theme's
     "Desktop rail starts: open" setting. [data-air-boot] on the shell suppresses the
     slide-in and page-shift transitions (DS contract, see docs/components/product-rail
     "Starting open"), so the panel is simply THERE instead of animating in on every
     navigation. Removed after two frames: one for the styles to apply without
     transition, the next so real interactions transition again. */
  function bootOpen(railEl) {
    var sh = shell(railEl);
    if (sh) sh.setAttribute('data-air-boot', '');
    open(railEl, false);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (sh) sh.removeAttribute('data-air-boot');
      });
    });
  }

  function restore() {
    var railEl = rail();
    if (!railEl) return;
    var remembered = null;
    try {
      remembered = sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      remembered = null;
    }

    /* Precedence: the VISITOR's session choice beats the merchant default.
         '1'  they had it open        -> restore it
         '0'  they closed it          -> stays closed, whatever the theme says
         null no interaction yet      -> follow settings.product_rail_desktop_state,
                                         carried on data-aero-rail-default.
       The default only applies from 1024px up — the same breakpoint that shows the
       edge tab. Below 640px the DS falls back to an overlay, and an unrequested
       overlay covering the page on arrival is hostile; 640-1023 is bubble territory.
       Note the boot open persists '1' (open() persists unconditionally), so the
       session keeps its state across resizes and later setting flips. */
    var wantsDefaultOpen =
      remembered === null &&
      railEl.getAttribute('data-aero-rail-default') === 'open' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 1024px)').matches;

    if (remembered === '1' || wantsDefaultOpen) bootOpen(railEl);
    else syncChrome(false);
  }

  /* --- triggers ------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-aero-product-rail-open]');
    if (opener) {
      event.preventDefault();
      var target = document.querySelector(opener.getAttribute('data-aero-product-rail-open')) || rail();
      if (isOpen(target)) close(target);
      else open(target);
      return;
    }
    if (event.target.closest('[data-aero-product-rail-close]')) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen(rail())) close();
  });

  /* --- dismiss on navigating away ------------------------------------------- */
  /* "View all products" leaves the page, so closing the rail is NOT about the
     animation — nobody sees it. It is about the PERSISTED state: the open/closed
     choice lives in sessionStorage, so without this the visitor lands on
     /pages/shop with the rail still docked, shrinking the very page they went
     there to browse. close() calls persist(false), which is the part that matters.

     Modified clicks are left alone. cmd/ctrl/shift-click and middle-click open the
     shop in a BACKGROUND tab; the visitor is still looking at this page, so
     collapsing the rail out from under them would be wrong. Only a plain
     same-tab activation dismisses it.

     No preventDefault and no delay: sessionStorage.setItem is synchronous and
     runs before the browser starts the navigation. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-aero-product-rail-dismiss]');
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    close(null, false);
  });

  /* --- one panel at a time -------------------------------------------------- */
  /* The cart drawer opens on [data-air-cart-drawer-open] clicks and after a successful
     add (aero:cart:added). Collapse the rail in both cases so the page never tries to
     honour two shifts, and so the cart is not competing with the rail for the edge. */
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-air-cart-drawer-open]')) close(null, false);
  }, true);

  document.addEventListener('aero:cart:added', function () {
    close(null, false);
  });

  /* --- size chips on a tile ------------------------------------------------- */
  /* Scoped to [data-aero-tile-buy] rather than to the rail, so any tile rendered with
     `add_to_cart: true` gets this for free wherever it lives.

     Deliberately NOT reusing the size logic in global.js: that reads a
     [data-aero-product-config] JSON blob per root (the whole `product.variants` array),
     which the PDP and quickview each emit once. The rail renders three tiles on EVERY page
     of the site, so three variant dumps would ship on every request to support two chips.
     The chips carry what they need on themselves instead.

     Each chip knows its variant id, price, compare price, availability and Amazon href, so
     selecting one is a local swap: move the hidden input, repaint the price, re-decide which
     cart control is visible using the same rule the server used, and re-point the Amazon
     link — which is always visible, so it is only ever re-pointed, never toggled. */
  function selectSize(chip) {
    var form = chip.closest('[data-aero-tile-buy]');
    if (!form) return;

    var tile = form.closest('.air-product-tile') || form;
    var sellsDirect = form.getAttribute('data-sells-direct') === 'true';
    var available = chip.getAttribute('data-available') === 'true';
    var amazonHref = chip.getAttribute('data-amazon') || '';

    /* pressed state — aria-pressed is the source of truth, the class is the paint */
    form.querySelectorAll('[data-aero-tile-size]').forEach(function (btn) {
      var on = btn === chip;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('air-btn--selected', on);
    });

    var input = form.querySelector('[data-aero-tile-variant]');
    if (input) input.value = chip.getAttribute('data-variant-id');

    var price = tile.querySelector('[data-aero-tile-price]');
    if (price) price.textContent = chip.getAttribute('data-price') || '';

    /* The compare price only EXISTS in the markup if the server-selected size had one, so
       a size that introduces a discount cannot grow the element. Hide it rather than
       inject: showing a stale strikethrough price would misstate the saving. */
    var compare = tile.querySelector('[data-aero-tile-compare]');
    if (compare) {
      var cmp = chip.getAttribute('data-compare') || '';
      compare.textContent = cmp;
      compare.hidden = cmp === '';
    }

    /* Same decision the Liquid makes, kept in the same order so the two cannot drift.
       Amazon is no longer part of it (2026-09-03): the button is always visible, so only
       the cart-side pair flips, and Sold out is now the exact inverse of Add to Cart. */
    var showAtc = sellsDirect && available;

    var atc = form.querySelector('[data-aero-tile-atc]');
    if (atc) atc.hidden = !showAtc;

    /* Re-point only. The chip's data-amazon already carries the product-level fallback, so
       an empty value means the product has no listing at all — in which case the Liquid
       rendered no button and there is nothing here to point anywhere. Guarding on it keeps
       a blank chip from blanking a good href. */
    var amazon = form.querySelector('[data-aero-tile-amazon]');
    if (amazon && amazonHref) amazon.setAttribute('href', amazonHref);

    /* Not the plain inverse of Add to Cart: "Sold out" is a claim about THIS store's
       stock, so a market that does not sell direct must not make it. Mirrors
       `tile_show_soldout` in the Liquid. */
    var soldOut = form.querySelector('[data-aero-tile-soldout]');
    if (soldOut) soldOut.hidden = !sellsDirect || showAtc;
  }

  document.addEventListener('click', function (event) {
    var chip = event.target.closest('[data-aero-tile-size]');
    if (chip) selectSize(chip);
  });

  /* --- standalone click telemetry --------------------------------------------- */
  /* The rail owns its click telemetry outright. It mirrors the A/B framework's
     trackEvent() EXACTLY (dataLayer push + gtag + the Clarity tag subset), so
     the existing GTM trigger, GA4 event and Query 5 export keep working with
     no pipeline change. Two deliberate differences:
       - scoped to clicks INSIDE this rail's own wrapper;
       - no `variant`: the test is closed, and inventing one would pollute the
         user-scoped attribution the framework owns.

     FULLY DECOUPLED 2026-09-08. This used to key off [data-ab-section] /
     [data-ab-track] and stand down whenever window.AeroABTestWired was set,
     letting the framework's delegated listener take over. Both are gone:

       - the attributes moved to data-aero-rail-telemetry / data-aero-track, so
         the framework can no longer see these clicks at all;
       - the stand-down guard was removed with them. Keeping it would now only
         ever DISABLE working telemetry, because nothing else fires these.

     The reason for the split is anti-flicker: the framework hides every
     [data-ab-section] until JS marks the document .ab-ready. While the rail was
     one, switching "Run site-wide A/B tests" back on would have put the whole
     rail behind a JS gate and destroyed its fail-open / no-JS render. */
  var TELEMETRY_SECTION = '[data-aero-rail-telemetry]';

  function railTrackEvent(name, data) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: name };
    for (var k in data) { if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k]; }
    window.dataLayer.push(payload);
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', name, data); } catch (e) { /* analytics must never break the page */ }
    }
    if (typeof window.clarity === 'function') {
      try {
        window.clarity('set', name, JSON.stringify(data));
        if (data.test_id) window.clarity('set', 'ab_test', String(data.test_id));
        if (data.element_name) window.clarity('set', 'ab_element', String(data.element_name));
        window.clarity('event', name);
      } catch (e) { /* ditto */ }
    }
  }

  document.addEventListener('click', function (event) {
    var el = event.target && event.target.closest ? event.target.closest('[data-aero-track]') : null;
    if (!el || !el.closest(TELEMETRY_SECTION)) return;

    var spec = el.getAttribute('data-aero-track') || 'click';
    var parts = spec.split(':');
    railTrackEvent('ab_test_interaction', {
      test_id: 'product_rail',
      test_name: 'Persistent product rail (telemetry)',
      test_type: 'telemetry',
      interaction_type: parts[0] || 'click',
      element_name: parts[1] || '',
      page_type: 'site_wide',
      timestamp: new Date().toISOString()
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restore);
  } else {
    restore();
  }

  window.AeroProductRail = { open: open, close: close, isOpen: function () { return isOpen(rail()); } };
})();
